/**
 * The door to the chat's new-design skin (prest0.ai/prest0design/, design-system §7a.3).
 *
 * ON THE FIRST DOWNLOAD, so it holds nothing but the one dynamic import of the `pavilion-chat` chunk and
 * the design check. src/index.tsx calls it right after startDesignPreview(), before the first render: the
 * sheet's request is on the wire before the chat engine's (which waits for the page to paint), and it is a
 * fraction of the engine's size, so the chat arrives already dressed. It loads wherever the new design's
 * chat shows — the tree, and prest0.ai while config/site-design.json lists `chat` as live (since
 * 2026-09-24); anywhere else it does nothing — no request, no style, no attribute (the `pavilion-chat` row
 * of scripts/check-chat-first-paint.mjs keeps the sheet itself off the first download and the engine).
 *
 * A FAILED DOWNLOAD NEVER HOLDS THE CHAT, AND NEVER RELOADS THE PAGE: it is tried twice, and if it still
 * cannot be had the chat simply keeps today's look, working. Deliberately NOT retryImport — its last resort
 * is the stale-tab self-heal, a page reload, which is right for a chunk the app needs and wrong for a skin:
 * a person must never lose their place because a stylesheet did not arrive.
 *
 * AND A FAILED DOWNLOAD IS QUIET (2026-09-25). Each failure is marked as owned recovery
 * (noteChunkRecoveryActivity), because webpack's prefetch runtime leaves one more promise per chunk load with
 * no rejection handler: without the mark, App.tsx's global handler logged "no active lazyWithRetry recovery"
 * and Chrome reported an uncaught ChunkLoadError page error for a failure this door had already absorbed
 * (measured on prest0.ai with the chunk served as html). The mark is only the recovery signal; retryImport's
 * reload is not adopted.
 */
import { designShows } from './previewPath';
import { preloadPavilionFont } from './pavilion/pavilionFont';
import { noteChunkRecoveryActivity } from '../utils/lazyWithRetry';

const SKIN_RETRY_DELAY_MS = 1500;

const loadSkin = () => import(/* webpackChunkName: "pavilion-chat" */ './pavilion/chat/pavilionChatSkin');

let started: Promise<void> | null = null;

/** Start loading the chat's skin, once, wherever the new design's chat shows. Returns the load (tests await it); null elsewhere. */
export function loadPavilionChatSkin(): Promise<void> | null {
  if (started) return started;
  if (!designShows('chat')) return null;
  preloadPavilionFont();
  started = loadSkin()
    // once more after a pause (webpack forgets a failed chunk, so the second import asks again). Marked first:
    // this handler runs before webpack's own unhandled copy of the rejection reaches App.tsx's global handler.
    .catch((error: unknown) => {
      noteChunkRecoveryActivity(error);
      return new Promise<void>((resolve) => setTimeout(resolve, SKIN_RETRY_DELAY_MS)).then(loadSkin);
    })
    .then(
      (mod) => {
        mod.installPavilionChatSkin();
      },
      (error: unknown) => {
        noteChunkRecoveryActivity(error);
        /* today's look, working: a skin never holds the chat */
      },
    );
  return started;
}

/** Tests only: forget the load so the next call starts again. */
export function resetPavilionChatSkinForTests(): void {
  started = null;
}
